import { useState } from 'react'
import { Linkedin, Twitter, Instagram, Github, Mail, Phone, MapPin, Send, ArrowRight, Shield, Lock, Cpu, Globe, Sparkles } from 'lucide-react'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)

  const handleSubscribe = async (e) => {
    e.preventDefault()
    if (!email || isSubmitting) return

    setIsSubmitting(true)
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    setIsSubscribed(true)
    setEmail('')
    setIsSubmitting(false)
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubscribed(false), 5000)
  }

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn', color: 'hover:text-[#0A66C2]' },
    { icon: Twitter, href: '#', label: 'Twitter', color: 'hover:text-[#1DA1F2]' },
    { icon: Instagram, href: '#', label: 'Instagram', color: 'hover:text-[#E4405F]' },
    { icon: Github, href: '#', label: 'GitHub', color: 'hover:text-gray-300' },
  ]

  const quickLinks = [
    { name: 'Services', href: '#services', desc: 'Our Solutions' },
    { name: 'Team', href: '#team', desc: 'Meet The Minds' },
    { name: 'Careers', href: '#recruitment', desc: 'Join The Mission' },
    { name: 'Contact', href: '#interface', desc: 'Initiate Protocol' },
  ]

  const legalLinks = [
    { name: 'Privacy Protocol', href: '#' },
    { name: 'Terms of Engagement', href: '#' },
    { name: 'Sitemap', href: '#' },
    { name: 'Cookie Policy', href: '#' },
  ]

  const techStack = [
    { name: 'AI/ML', color: 'text-purple-400' },
    { name: 'Cloud', color: 'text-blue-400' },
    { name: 'Blockchain', color: 'text-green-400' },
    { name: 'IoT', color: 'text-cyan-400' },
    { name: 'Cybersecurity', color: 'text-red-400' },
    { name: 'Quantum', color: 'text-yellow-400' },
  ]

  return (
    <footer className="relative bg-gradient-to-b from-gray-950 via-black to-gray-950 border-t border-gray-800/50">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl"></div>
        
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(56, 189, 248, 0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(56, 189, 248, 0.1) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
          {/* Brand Column - Span 4 */}
          <div className="lg:col-span-4 space-y-6">
            <div className="flex items-center space-x-3">
              {/* Logo Container - Only change made */}
              <div className="relative w-40 h-17 flex items-center justify-center">
                <img 
                  src="/assets/images/logos/quavrion-logo.png" 
                  alt="Quavrion Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const target = e.target;
                    console.error('Failed to load logo image');
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="relative">
                          <div class="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-500 blur-lg opacity-50"></div>
                          <div class="relative w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center border border-cyan-400/30">
                            <span class="text-gray-900 font-bold text-xl">Q</span>
                          </div>
                        </div>
                      `;
                    }
                  }}
                />
              </div>
              {/* <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent tracking-wider">
                  QUAVRION
                </span>
                <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">TECHNOLOGIES</p>
              </div> */}
            </div>
            
            <p className="text-gray-400 text-sm leading-relaxed max-w-md">
              Engineering tomorrow's digital ecosystems with precision, innovation, and silent execution. 
              We architect the foundations upon which visionary enterprises build their future.
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-400 font-medium">Systems Online</span>
              </div>
              <div className="flex items-center space-x-2">
                <Lock className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-gray-400">End-to-End Encrypted</span>
              </div>
            </div>

            <div className="flex space-x-4 pt-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className={`w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 ${social.color} transition-all duration-300 hover:scale-110 hover:border-white/20`}
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links - Span 3 */}
          <div className="lg:col-span-3">
            <div className="flex items-center space-x-2 mb-6">
              <Cpu className="w-5 h-5 text-cyan-400" />
              <h3 className="text-lg font-bold text-white">
                QUICK ACCESS
              </h3>
            </div>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="group flex flex-col hover:bg-white/5 rounded-lg p-3 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300 group-hover:text-cyan-400 transition-colors font-medium">
                        {link.name}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                      {link.desc}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Span 3 */}
          <div className="lg:col-span-3">
            <div className="flex items-center space-x-2 mb-6">
              <Globe className="w-5 h-5 text-cyan-400" />
              <h3 className="text-lg font-bold text-white">
                COORDINATES
              </h3>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 p-3 hover:bg-white/5 rounded-lg transition-colors">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium">HQ Location</p>
                  <p className="text-gray-500 text-xs mt-1">
                    Hyderabad, Telangana<br />
                    India • 500032
                  </p>
                </div>
              </li>
              
              <li className="flex items-start space-x-3 p-3 hover:bg-white/5 rounded-lg transition-colors">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium">Contact</p>
                  <a href="mailto:hello@quavrion.com" className="text-cyan-400 text-xs mt-1 hover:text-cyan-300 transition-colors">
                    hello@quavrion.com
                  </a>
                </div>
              </li>
              
              <li className="flex items-start space-x-3 p-3 hover:bg-white/5 rounded-lg transition-colors">
                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <p className="text-gray-300 text-sm font-medium">Direct Line</p>
                  <a href="tel:+919876543210" className="text-cyan-400 text-xs mt-1 hover:text-cyan-300 transition-colors">
                    +91 98765 43210
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Newsletter - Span 2 */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              <h3 className="text-lg font-bold text-white">
                PROTOCOL UPDATES
              </h3>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-400 text-sm">
                Subscribe to receive intelligence updates, tech insights, and project launches.
              </p>
              
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.frequency@domain.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all rounded-lg"
                    required
                  />
                  <Send className="absolute right-3 top-3 w-4 h-4 text-gray-500" />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 rounded-lg font-medium text-sm transition-all duration-300 flex items-center justify-center space-x-2 ${
                    isSubmitting
                      ? 'bg-gray-800 cursor-not-allowed'
                      : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-lg hover:shadow-cyan-500/25 text-white'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>PROCESSING...</span>
                    </>
                  ) : isSubscribed ? (
                    <span className="text-green-400">✓ SUBSCRIBED</span>
                  ) : (
                    <>
                      <span>SUBSCRIBE</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
              
              {isSubscribed && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-sm text-center">
                    Successfully subscribed to protocol updates
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tech Stack Showcase */}
        {/* <div className="border-t border-gray-800/50 pt-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">TECH STACK</h4>
            <Shield className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="flex flex-wrap gap-3">
            {techStack.map((tech) => (
              <div
                key={tech.name}
                className="px-3 py-2 bg-white/5 rounded-lg border border-white/10 hover:border-cyan-500/30 transition-colors"
              >
                <span className={`text-xs font-medium ${tech.color}`}>
                  {tech.name}
                </span>
              </div>
            ))}
          </div>
        </div> */}

        {/* Bottom Bar */}
        <div className="border-t border-gray-800/50 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
            {/* Copyright */}
            <div className="text-center lg:text-left">
              <div className="text-gray-500 text-sm mb-1">
                © {currentYear} QUAVRION TECHNOLOGIES. ALL SYSTEMS OPERATIONAL.
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-600">
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                  <span>UPTIME: 99.99%</span>
                </div>
                <span>•</span>
                <span>ENCRYPTION: AES-256</span>
                <span>•</span>
                <span>PROTOCOL: v2.1.4</span>
              </div>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap justify-center gap-6">
              {legalLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-gray-500 hover:text-cyan-400 text-sm transition-colors hover:underline underline-offset-4"
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* Status Indicator */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-400 font-medium">SYSTEMS: ONLINE</span>
              </div>
              <div className="text-xs text-gray-500 px-3 py-1 bg-white/5 rounded-full">
                UTC {new Date().getUTCHours().toString().padStart(2, '0')}:{new Date().getUTCMinutes().toString().padStart(2, '0')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer